/**
 * Adapters from the two image storage backends onto the single shape the mastpoint
 * gallery renders. Keeping the branch here means components never learn which
 * backend served them, so retiring `MastPointFile` is a deletion rather than a
 * refactor.
 */
import type { IMastPointImage } from '$lib/models/IMastPointImage';
import type { IPoleImage } from '$lib/models/IPoleImage';
import type { ISmartData } from '$lib/models/ISmartData';

/**
 * Lowercased, dot-prefixed extension from a file name, e.g. `.jpg`. The
 * FileManager image model has no `fileExtension` field, so it is derived from the
 * file name instead. Empty string when there is nothing to derive from.
 */
export const fileExtensionFrom = (name: string): string => {
	const lastDot = name.lastIndexOf('.');
	// `< 1` also rejects dotfiles with no stem, where the dot is not a separator.
	if (lastDot < 1) return '';
	return name.slice(lastDot).toLowerCase();
};

/**
 * Legacy storage (`MastPointFile`) has no compressed derivatives, so every URL
 * field points at the original. The gallery keeps working exactly as before — it
 * just does not get any lighter until the company is migrated.
 */
export const fromMastPointFile = (image: IPoleImage): IMastPointImage => ({
	id: image.id,
	description: image.description,
	createdAt: image.createdAt,
	thumbnailUrl: image.fileUri,
	previewUrl: image.fileUri,
	originalUrl: image.fileUri,
	fileExtension: image.fileExtension?.toLowerCase() ?? '',
	hasPreview: false,
	// The legacy payload types these as plain numbers and uses a falsy value for
	// "not linked", so `|| null` normalizes both `0` and a missing value.
	conditionId: image.conditionId || null,
	planId: image.planId || null,
	isProcessing: false
});

/**
 * New storage supplies compressed `.webp` derivatives. Each URL falls back to the
 * next coarser one, so an image whose derivatives are not ready yet still renders
 * from the original instead of showing nothing.
 */
export const fromFileManagerImage = (image: ISmartData.ImageItem): IMastPointImage => {
	const originalUrl = image.originalUrl ?? '';
	const previewUrl = image.url ?? originalUrl;

	return {
		id: image.id,
		description: image.description ?? '',
		createdAt: image.createdAt,
		thumbnailUrl: image.thumbnailUrl ?? previewUrl,
		previewUrl,
		originalUrl,
		fileExtension: fileExtensionFrom(image.originalName ?? image.displayName ?? ''),
		// Non-image files (PDFs and the like) come back without derivatives, which is
		// what keeps them out of the gallery.
		hasPreview: Boolean(image.thumbnailUrl || image.url),
		conditionId: image.conditionId ?? null,
		planId: image.planId ?? null,
		isProcessing: image.status === 'Processing'
	};
};

/**
 * Newest first — the order the gallery has always displayed. Replaces an in-place
 * `data.reverse()` that used to run inside the query's `onSuccess`, which mutated
 * cached data and silently disappeared whenever a caller passed its own options.
 *
 * Parsed rather than string-compared: these are `DateTimeOffset` values, so two
 * timestamps with different UTC offsets do not sort correctly as text.
 */
export const newestFirst = (images: IMastPointImage[]): IMastPointImage[] =>
	[...images].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
